import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { AI_MODELS } from '@/components/AIModelSelector';
import { 
  Copy, 
  Check, 
  Loader2, 
  Lock, 
  RefreshCw,
  Trash2,
  ArrowRight,
  Columns,
  Sparkles,
  X
} from 'lucide-react';

// Upgrade Modal for non-Pro users
const UpgradeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  
  const handleUpgrade = () => {
    onClose();
    navigate('/settings', { state: { selectedPlan: 'pro' } });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Columns className="w-5 h-5 text-primary" />
            View Full Comparison
          </DialogTitle>
          <DialogDescription className="pt-2">
            Access detailed prompt outputs and comparisons anytime.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center text-white">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">Pro Feature</p>
                <p className="text-sm text-muted-foreground">
                  View and manage saved comparisons
                </p>
              </div>
            </div>
          </div>
          
          <ul className="text-sm text-muted-foreground space-y-1.5 pl-4 mt-4">
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-green-500" />
              <span>View full model outputs</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-green-500" />
              <span>Copy any result instantly</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-green-500" />
              <span>Regenerate comparisons</span>
            </li>
          </ul>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            Maybe Later
          </Button>
          <Button onClick={handleUpgrade} className="gap-2">
            Upgrade to Pro
            <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Model Output Card
const ModelOutputCard = ({ modelId, output, onCopy, copied }) => {
  const model = AI_MODELS.find(m => m.id === modelId) || { 
    id: modelId, 
    name: modelId.toUpperCase(), 
    icon: '🤖',
    description: 'AI Model'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-muted/30 rounded-xl p-4 border border-border"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{model.icon}</span>
          <span className="font-medium text-sm">{model.name}</span>
        </div>
        {output?.highlight && (
          <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">
            {output.highlight}
          </span>
        )}
      </div>
      
      <p className="text-sm leading-relaxed text-muted-foreground mb-3 min-h-[60px]">
        {output?.enhanced || 'No output available'}
      </p>
      
      <Button
        variant="ghost"
        size="sm"
        className="w-full h-8 text-xs"
        onClick={() => onCopy(output?.enhanced, modelId)}
      >
        {copied ? (
          <>
            <Check className="w-3 h-3 mr-1.5 text-green-500" />
            Copied
          </>
        ) : (
          <>
            <Copy className="w-3 h-3 mr-1.5" />
            Copy Output
          </>
        )}
      </Button>
    </motion.div>
  );
};

// Main Comparison Detail Modal
const ComparisonDetailModal = ({ 
  isOpen, 
  onClose, 
  comparison,
  onDelete,
  onRegenerate
}) => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  const [copied, setCopied] = useState({});
  const [regenerating, setRegenerating] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const userPlan = user?.plan || 'free';
  const canAccess = userPlan === 'pro';
  
  // If non-Pro user tries to open, show upgrade modal
  if (!canAccess && isOpen) {
    return <UpgradeModal isOpen={isOpen} onClose={onClose} />;
  }
  
  if (!comparison) return null;
  
  const { input_data, output_data, created_at, id } = comparison;
  const originalPrompt = input_data?.original_prompt || 'No prompt';
  const style = input_data?.style || 'general';
  const comparisons = output_data?.comparisons || {};
  const modelsUsed = output_data?.models_used || Object.keys(comparisons);
  
  const copyToClipboard = async (text, key) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
  };
  
  const handleRegenerate = async () => {
    if (!onRegenerate) {
      // Navigate to prompt enhancer with the prompt pre-filled
      navigate('/dashboard/prompt-enhancer', { 
        state: { 
          prompt: originalPrompt, 
          style,
          openCompare: true 
        } 
      });
      onClose();
      return;
    }
    
    setRegenerating(true);
    try {
      await onRegenerate(comparison);
      toast.success('Comparison regenerated!');
      onClose();
    } catch (error) {
      toast.error('Failed to regenerate comparison');
    } finally {
      setRegenerating(false);
    }
  };
  
  const handleDelete = async () => {
    if (!onDelete) return;
    
    setDeleting(true);
    try {
      await onDelete(id);
      toast.success('Comparison deleted');
      onClose();
    } catch (error) {
      toast.error('Failed to delete comparison');
    } finally {
      setDeleting(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Columns className="w-5 h-5 text-primary" />
            Comparison Details
          </DialogTitle>
          <DialogDescription>
            {formatDate(created_at)}
          </DialogDescription>
        </DialogHeader>
        
        {/* Original Prompt Section */}
        <div className="space-y-4">
          <div className="bg-muted/30 rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Original Prompt</span>
              </div>
              <span className="text-xs bg-muted px-2 py-0.5 rounded capitalize">
                {style}
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {originalPrompt}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-7 text-xs"
              onClick={() => copyToClipboard(originalPrompt, 'prompt')}
            >
              {copied['prompt'] ? (
                <>
                  <Check className="w-3 h-3 mr-1.5 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3 mr-1.5" />
                  Copy Prompt
                </>
              )}
            </Button>
          </div>
          
          {/* Model Outputs Grid */}
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <span>Model Outputs</span>
              <span className="text-xs text-muted-foreground">
                ({modelsUsed.length} model{modelsUsed.length !== 1 ? 's' : ''})
              </span>
            </h4>
            
            <div className={`grid gap-3 ${
              modelsUsed.length === 1 ? 'grid-cols-1' :
              modelsUsed.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
              modelsUsed.length === 3 ? 'grid-cols-1 sm:grid-cols-3' :
              'grid-cols-1 sm:grid-cols-2'
            }`}>
              {modelsUsed.map((modelId, index) => (
                <ModelOutputCard
                  key={modelId}
                  modelId={modelId}
                  output={comparisons[modelId]}
                  onCopy={copyToClipboard}
                  copied={copied[modelId]}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Actions Footer */}
        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4 pt-4 border-t border-border">
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRegenerate}
              disabled={regenerating}
              className="flex-1 sm:flex-none"
            >
              {regenerating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Regenerate
            </Button>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive flex-1 sm:flex-none"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Comparison?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove this comparison from your gallery.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground"
                    disabled={deleting}
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          
          <Button variant="ghost" onClick={onClose} className="sm:ml-auto">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComparisonDetailModal;
